import ArgumentWrapper from '@/components/arguments/ArgumentWrapper';
import Panel from '@/components/panel/Panel';
import { FiActivity, FiZap } from '@/components/react-icons/main';
import getParallelLoopBlockerLabel from '@/screens/workflows/lib/getParallelLoopBlockerLabel';
import stepActions from '@/screens/workflows/redux/actions/stepActions';
import parallelLoopBlockersSelector from '@/screens/workflows/redux/selectors/parallelLoopBlockersSelector';
import workflowScreenSelectors from '@/screens/workflows/redux/selectors/workflowScreenSelectors';
import { PARALLEL_LOOP_MAX_WINDOW } from '@shared/workflow/parallelLoopMaxWindow.js';
import { useSelector } from 'react-redux';
import SliderClose from './SliderClose';
import SliderHeader from './SliderHeader';
import SliderTitle from './SliderTitle';

const BEHAVIOR_NOTES = ['Items start in list order; up to the chosen number run at once.', 'If one item fails, items already running finish and no new ones start.', 'Results after the loop are the same as running one item at a time.', 'Steps that write to other systems may run in a different order than the list.', 'Loop Delay is the pause each slot takes between its items.'].join('\n');

const clampWindow = (value) => Math.min(Math.max(parseInt(value, 10) || 1, 1), PARALLEL_LOOP_MAX_WINDOW);

const ParallelSlider = ({ onClose, disabled = false }) => {
  const parallel = useSelector((state) => workflowScreenSelectors.activeWorkflowStep(state)?.options?.parallel);
  const steps = useSelector((state) => workflowScreenSelectors.activeWorkflow(state)?.steps);
  const blockers = useSelector(parallelLoopBlockersSelector);

  const enabled = parallel?.enabled === true;
  const adaptive = parallel?.adaptive !== false;
  const parallelWindow = parallel?.parallelWindow ?? 1;
  const maxParallelWindow = parallel?.maxParallelWindow ?? 5;
  const hasBlockers = blockers.length > 0;

  const handleChange = (key, value) => {
    stepActions.set('options.parallel', (current) => ({
      ...current,
      [key]: value,
      ...(key === 'adaptive' && value ? { parallelWindow: 1 } : {})
    }));
  };

  const handleDelete = () => {
    stepActions.set('options.parallel', null);
    onClose();
  };

  return (
    <>
      <SliderHeader onClose={onClose} showDelete={!!parallel} onDelete={handleDelete} disabled={disabled} />
      <SliderTitle //
        icon={FiZap}
        title="Parallel Processing"
        description="Run several items of this loop at the same time."
      />
      <Panel.Section headerless onDelete={parallel ? handleDelete : null}>
        {hasBlockers && (
          <Panel.Alert //
            variant="warn"
            title={enabled ? 'This loop will run one item at a time' : 'This loop cannot run in parallel yet'}
            description={blockers.map((blocker) => getParallelLoopBlockerLabel({ blocker, steps })).join('\n')}
          />
        )}
        <ArgumentWrapper>
          <Panel.Toggle //
            disabled={disabled || (hasBlockers && !enabled)}
            label="Process items in parallel?"
            icon={FiZap}
            active={enabled}
            onChange={(value) => handleChange('enabled', value)}
          />
        </ArgumentWrapper>
        {enabled && (
          <>
            <ArgumentWrapper>
              <Panel.Toggle //
                disabled={disabled}
                label="Adapt to the API?"
                icon={FiActivity}
                active={adaptive}
                onChange={(value) => handleChange('adaptive', value)}
                description="Start with one item at a time and speed up while requests succeed. Slow down when the API pushes back."
              />
            </ArgumentWrapper>
            <Panel.Input //
              disabled={disabled}
              label={adaptive ? 'Max items at a time' : 'Items at a time'}
              description={`Between 1 and ${PARALLEL_LOOP_MAX_WINDOW}.`}
              value={adaptive ? maxParallelWindow : parallelWindow}
              onChange={(e) => handleChange(adaptive ? 'maxParallelWindow' : 'parallelWindow', clampWindow(e.target.value))}
              type="number"
              min={1}
              max={PARALLEL_LOOP_MAX_WINDOW}
            />
            <Panel.Alert variant="info" description={BEHAVIOR_NOTES} />
          </>
        )}
      </Panel.Section>
      <SliderClose onClose={onClose} />
    </>
  );
};

export default ParallelSlider;
